import sha1 from "crypto-js/sha1";
import 'firebase/compat/firestore';
import { Artist, ArtistId } from "../models/Artist";
import { Song } from "../models/Song";
import { EndOfTime, Timestamp } from "../models/Timeline";
import { OwnerId } from "../models/User";
import handleError from "../util/handleError";
import { SongService } from "./SongService";

import 'firebase/compat/firestore';
import firebase from "firebase/compat/app";
import { getViewer } from "./Viewer";
import { ARTIST_PRIVACY_ENFORCED } from "../config/env";
const firestore = firebase.firestore

export class ArtistService {
  /**
   * The set of provably-readable queries over an artist's songs. A bare
   * `authors array-contains` query is denied by the privacy rules (the
   * result set could contain restricted docs), so we query one readable
   * slice at a time and merge:
   *  - staff: single unfiltered query (isStaff() passes rules per se)
   *  - everyone: the tiers applyGlobalPrivacy allows for this viewer
   *  - the artist themself: plus an `ownerId == uid` query so owners always
   *    see all of their own songs regardless of tier
   */
  private static artistSongQueries = (artistId: ArtistId): firebase.firestore.Query[] => {
    const db = firestore();
    const viewer = getViewer();
    const base = db.collection("songs").where("authors", "array-contains", artistId);
    if (viewer.staff) return [base];
    const queries = [SongService.applyGlobalPrivacy(base, viewer)];
    const user = firebase.auth().currentUser;
    const email = user?.email ?? user?.providerData?.[0]?.email;
    const isSelf = !!email && ArtistService.calculateArtistId(email) === artistId;
    if (isSelf && viewer.uid) {
      queries.push(base.where("ownerId", "==", viewer.uid));
    }
    return queries;
  };

  private static mergeSongSnapshots = (
    snapshots: firebase.firestore.QuerySnapshot[],
  ): Song[] => {
    const byId: Record<string, Song> = {};
    snapshots.forEach((snap) =>
      snap.docs.forEach((d) => {
        byId[d.id] = SongService.StronglyTypeSongData(d);
      }),
    );
    return Object.values(byId).sort(
      (a, b) =>
        ((a.updatedAt as any)?.seconds ?? 0) - ((b.updatedAt as any)?.seconds ?? 0),
    );
  };

  /**
   * Fetch latest song uploaded by artist
   * @param ownerId
   */
  static fetchLatestSong(ownerId: OwnerId): Promise<Song> {
    const db = firestore();
    const viewer = getViewer();
    let query = db.collection("songs").where("ownerId", "==", ownerId);
    // Own songs are provable via the ownerId pin; anyone else's need a
    // provable privacy filter.
    if (!viewer.staff && viewer.uid !== ownerId) {
      query = SongService.applyGlobalPrivacy(query, viewer);
    }
    return new Promise((resolve, reject) => {
      query
        .orderBy("updatedAt")
        .limitToLast(1)
        .get()
        .then((songDocs) => {
          const latestSongData = songDocs.docs.pop();
          if (latestSongData) {
            resolve(SongService.StronglyTypeSongData(latestSongData));
          } else {
            resolve({} as Song);
          }
        })
        .catch(reject);
    });
  }

  /**
   * fetch artist with meta data
   * @param id artist id
   */
  static fetch = (id: string) => {
    return new Promise<Artist>((resolve, reject) => {
      var db = firestore();
      if (typeof id === undefined || id === "" || typeof id != "string") {
        return reject('bad artist id');
      }
      db.collection("artists")
        .doc(id)
        .get()
        .then((artistDoc) => {
          
          const artist = ArtistService.StronglyTypeArtistData(artistDoc);
          if (artistDoc.exists) {
            resolve(artist);
          } else {
            reject({message:"not-found"});
          }
        })
        // A members-only profile is denied to anonymous viewers; settle the
        // promise instead of hanging forever.
        .catch(reject);
    });
  };
  /**
   * Fetch multiple artists by their ownerIds in batches
   * @param ownerIds Array of owner IDs to fetch
   * @param batchSize Size of each batch (default 30)
   */
  static fetchMultipleByOwnerIds = async (
    ownerIds: string[],
    batchSize: number = 30
  ): Promise<Artist[]> => {

  // Utility function to chunk array into smaller arrays
const chunk:any = <T>(array: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
    array.slice(i * size, i * size + size)
  );
};

    try {
      // Remove any invalid ownerIds
      const validOwnerIds = ownerIds.filter(
        (id): id is string => 
        typeof id === "string" && id !== "" && id !== undefined
      );

      // Split ownerIds into chunks
      const batches = chunk(validOwnerIds, batchSize);
      
      // Process each batch
      const batchResults = await Promise.all(
        batches.map(async (batchIds:any) => {
          const batchPromises = batchIds.map((ownerId:any) =>
            ArtistService.fetchByOwnerId(ownerId).catch((error) => {
              console.error(`Error fetching artist for ownerId ${ownerId}:`, error);
              return null; // Return null for failed fetches
            })
          );
          
          return Promise.all(batchPromises);
        })
      );

      // Flatten results and filter out nulls
      return batchResults
        .flat()
        .filter((artist:any): artist is Artist => artist !== null);
    } catch (error) {
      console.error("Error in batch fetching artists:", error);
      throw error;
    }
  };

 /**
   * Fetch multiple artists by their ownerIds in batches
   * @param ownerIds Array of owner IDs to fetch
   * @param batchSize Size of each batch (default 30)
   */
 static fetchMultipleByArtistIds = async (
  artistIds: string[],
  batchSize: number = 30
): Promise<Artist[]> => {

// Utility function to chunk array into smaller arrays
const chunk:any = <T>(array: T[], size: number): T[][] => {
return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
  array.slice(i * size, i * size + size)
);
};

  try {
    // Remove any invalid ownerIds
    const validArtistIds = artistIds.filter(
      (id): id is string => 
      typeof id === "string" && id !== "" && id !== undefined
    );

    // Split ownerIds into chunks
    const batches = chunk(validArtistIds, batchSize);
    
    // Process each batch
    const batchResults = await Promise.all(
      batches.map(async (batchIds:any) => {
        const batchPromises = batchIds.map((artistId:any) =>
          ArtistService.fetch(artistId).catch((error) => {
            console.error(`Error fetching artist for artistId ${artistId}:`, error);
            return null; // Return null for failed fetches
          })
        );
        
        return Promise.all(batchPromises);
      })
    );

    // Flatten results and filter out nulls
    return batchResults
      .flat()
      .filter((artist:any): artist is Artist => artist !== null);
  } catch (error) {
    console.error("Error in batch fetching artists:", error);
    throw error;
  }
};

  /**
   * fetch artist with meta data
   * @param ownerId artist Ownerid
   */
  static fetchByOwnerId = (ownerId: string) => {
    return new Promise<Artist>((resolve, reject) => {
      var db = firestore();
      if (
        typeof ownerId === undefined ||
        ownerId === "" ||
        typeof ownerId != "string"
      ) {
        console.log("bad owner id");
        return;
      }
      db.collection("artists")
        .where("ownerId", "==", ownerId)
        .limit(1)
        .get()
        .then((artistDocs) => {
          const artistDoc = artistDocs.docs.pop();
          if (!artistDoc) {
            reject("error");
          } else {
            const artist = { ...artistDoc.data(), id: artistDoc.id } as Artist;
            if (artist) {
              resolve(artist);
            }
          }
        })
        .catch(handleError);
    }).catch(handleError);
  };

  /**
   * Fetch some songs
   *
   * @param artistId the artist ID
   * @param limit how many songs to get
   */
  static fetchSomeSongs = async (artistId: ArtistId, limit: number) => {
    const queries = ArtistService.artistSongQueries(artistId);
    const snapshots = await Promise.all(
      queries.map((q) => q.orderBy("updatedAt").limitToLast(limit).get()),
    );
    // Merged slices can overshoot the page size; keep the NEWEST `limit`
    // (ascending order → take from the end) so the derived cursor
    // (oldestFromArtist) stays a gap-free window boundary — anything dropped
    // here is older than the cursor and refetches on the next page.
    return ArtistService.mergeSongSnapshots(snapshots).slice(-limit);
  };

  static fetchArtistSongsNumber = async (artistId: ArtistId) => {
    const queries = ArtistService.artistSongQueries(artistId);
    const snapshots = await Promise.all(queries.map((q) => q.get()));
    const ids = new Set<string>();
    snapshots.forEach((snap) => snap.docs.forEach((d) => ids.add(d.id)));
    return ids.size;
  };

  /**
   * Fetch more songs of a particular artist
   *
   * @param artistId the artist ID
   * @param timestamp timestamp of an old song to start from
   * @param limit how many songs to get
   */
  static fetchMoreSongs = async (
    artistId: ArtistId,
    timestamp: Timestamp,
    limit: number
  ) => {
    if (timestamp === EndOfTime || timestamp.seconds === -1) {
      return ArtistService.fetchSomeSongs(artistId, limit);
    }
    const queries = ArtistService.artistSongQueries(artistId);
    const snapshots = await Promise.all(
      queries.map((q) =>
        q.orderBy("updatedAt").endBefore(timestamp).limitToLast(limit).get(),
      ),
    );
    return ArtistService.mergeSongSnapshots(snapshots).slice(-limit);
  };

  /**
   * Calculate Arist ID using SHA1 hash
   * Will we ever run into collisions?
   * think about migrating to another function to avoid them....
   * @param limit
   * @param dispatch
   */
  static calculateArtistId(email: string): string {
    return sha1(email).toString().substring(0, 11);
  }


  static fetchArtistsForRegion = (regionId: string, pageSize: number, offset: number=0): Promise<Artist[]> => {
    console.log(regionId,offset,pageSize,)
    return new Promise((resolve, reject) => {
      const db = firestore()
      // Mirrors SongService.applyGlobalPrivacy: an unfiltered list query is
      // denied wholesale unless canReadArtist() is provably true for every
      // possible result, so anonymous callers need the explicit filter — but
      // only on instances that have deployed canReadArtist() + the backfill
      // + the composite index (see ARTIST_PRIVACY_ENFORCED's doc comment in
      // config/env.ts). Elsewhere the filter only breaks things.
      const authed = !ARTIST_PRIVACY_ENFORCED || getViewer().authed
      const regionBase = () => {
        let q = db.collection('artists')
          .orderBy("songs", "desc")
          .where('region', '==', regionId)
        return authed ? q : q.where('privacy', '==', 'public')
      }
      const query = regionBase()
        .limit(pageSize)

      if (offset > 0) {
        // Get the last document from the previous page
        regionBase()
          .limit(offset)
          .get()
          .then((snapshot) => {
            const lastVisible = snapshot.docs[snapshot.docs.length - 1]
            // Start after the last document from previous page
            query.startAfter(lastVisible)
              .get()
              .then((querySnapshot) => {
                const artistBatch = querySnapshot.docs.map((songDoc) =>
                  ArtistService.StronglyTypeArtistData(songDoc),
                )
                resolve(artistBatch)
              })
              .catch((e) => {
                console.log(e)
                reject(e)
              })
          })
          .catch((e) => {
            console.log(e)
            reject(e)
          })
      } else {
        // First page, no offset needed
        query.get()
          .then((querySnapshot) => {
            console.log("Got batch")
            const artistBatch = querySnapshot.docs.map((artistDoc) =>
              ArtistService.StronglyTypeArtistData(artistDoc),
            )
            console.log(artistBatch);

            resolve(artistBatch)
          })
          .catch((e) => {
            console.log(e)
            reject(e)
          })
      }
    })
  }
  static StronglyTypeArtistData = (
    artistDoc:
      | firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
      | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
  ) => {
    const artist_data = { ...artistDoc.data(), id: artistDoc.id } as Artist;
    return artist_data;
  };
}
